import {useMemo, useSyncExternalStore} from 'react';

import {globalWindow} from '../../../core/src/helpers/browser/browserHelpers';
import {prefersDarkMode$} from '../../../core/src/helpers/environment/darkMode';
import {UnreachableError} from '../../../core/src/helpers/types/customErrors';
import {UserThemesEnum} from '../../../core/src/models/api/userThemesEnum';
import {darkTheme as defaultDarkTheme} from './dark/darkTheme';
import {redesignedDarkTheme} from './dark/redesign/redesignedDarkTheme';
import {lightTheme as defaultLightTheme} from './light/lightTheme';
import {redesignedLightTheme} from './light/redesign/redesignedLightTheme';
import {UserTheme, UserThemeOptions} from './themeTypes';

const subscribeToPrefersDarkMode = (callback: () => void) => {
  const subscription = prefersDarkMode$.subscribe(callback);
  return () => subscription.unsubscribe();
};

const getPrefersDarkModeSnapshot = () => globalWindow.matchMedia('(prefers-color-scheme: dark)').matches;

export function usePrefersDarkMode() {
  return useSyncExternalStore(subscribeToPrefersDarkMode, getPrefersDarkModeSnapshot);
}

export function useUserThemeStyles(theme: UserThemesEnum, options: UserThemeOptions): UserTheme {
  const prefersDarkMode = usePrefersDarkMode();
  const lightTheme = options.hasInboxRedesign ? redesignedLightTheme : defaultLightTheme;
  const darkTheme = options.hasInboxRedesign ? redesignedDarkTheme : defaultDarkTheme;

  const resultTheme = useMemo<UserTheme>(() => {
    switch (theme) {
      case UserThemesEnum.LIGHT:
      case UserThemesEnum.DARK_SIDEBAR:
        return {
          ...lightTheme,
          hasDropdownRedesign: options.hasDropdownRedesign,
          hasP0AppSwitcher: options.hasP0AppSwitcher,
        };
      case UserThemesEnum.DARK:
        return {
          ...darkTheme,
          hasDropdownRedesign: options.hasDropdownRedesign,
          hasP0AppSwitcher: options.hasP0AppSwitcher,
        };
      case UserThemesEnum.SYSTEM:
        return {
          ...(prefersDarkMode ? darkTheme : lightTheme),
          hasDropdownRedesign: options.hasDropdownRedesign,
          hasP0AppSwitcher: options.hasP0AppSwitcher,
        };
      default:
        throw new UnreachableError(theme);
    }
  }, [darkTheme, lightTheme, prefersDarkMode, theme, options]);

  return resultTheme;
}
